<template>
  <a-modal
    :open="open"
    title="站点信息"
    width="720px"
    @update:open="(val) => emit('update:open', val)"
    @cancel="handleCancel"
  >
    <a-form :model="formData" :label-col="{ span: 5 }" :wrapper-col="{ span: 16 }">
      <a-form-item label="城市" required>
        <a-select v-model:value="formData.cityId" placeholder="请选择城市" style="width: 100%">
          <a-select-option v-for="city in cityList" :key="city.id" :value="city.id">
            {{ city.name }}
          </a-select-option>
        </a-select>
      </a-form-item>
      <a-form-item label="厂商" required>
        <a-select v-model:value="formData.providerCode" placeholder="请选择厂商" style="width: 100%">
          <a-select-option
            v-for="provider in providerList"
            :key="provider.code"
            :value="provider.code"
          >
            {{ provider.name }}
          </a-select-option>
        </a-select>
      </a-form-item>
      <a-form-item label="厂商站点ID" required>
        <a-input v-model:value="formData.providerStopId" placeholder="请输入厂商站点ID" />
      </a-form-item>
      <a-form-item label="站点名称" required>
        <a-input v-model:value="formData.name" placeholder="请输入站点名称" />
      </a-form-item>
      <a-form-item label="站点位置" required>
        <a-input v-model:value="formData.address" placeholder="请输入位置" />
      </a-form-item>
      <a-form-item label="经纬度" required>
        <a-input v-model:value="formData.coordinates" placeholder="格式：经度,纬度" />
      </a-form-item>
      <a-form-item label="经纬度（gcj02）" required>
        <a-input v-model:value="formData.gcj02Coordinates" placeholder="格式：经度,纬度" />
      </a-form-item>
    </a-form>
    <template #footer>
      <a-button @click="handleCancel">取消</a-button>
      <a-button type="primary" :loading="saving" @click="handleSave">确定</a-button>
    </template>
  </a-modal>
</template>

<script setup>
import { reactive, ref, watch } from "vue";
import { message } from "ant-design-vue";
import { addStop, updateStop } from "@/api/roads/stop.js";
import { getCityList } from "@/api/roads/city.js";
import { getProviderList } from "@/api/settings/provider.js";

const props = defineProps({
  open: { type: Boolean, default: false },
  initialData: { type: Object, default: null },
});

const emit = defineEmits(["update:open", "success"]);

const cityList = ref([]);
const providerList = ref([]);
const saving = ref(false);

const formData = reactive({
  id: null,
  cityId: null,
  providerCode: "",
  name: "",
  providerStopId: "",
  address: "",
  coordinates: "",
  gcj02Coordinates: "",
});

const loadCityList = async () => {
  try {
    const res = await getCityList();
    if (res.code === 200 && res.data) {
      cityList.value = res.data;
    }
  } catch (error) {
    console.error("获取城市列表失败:", error);
  }
};

const loadProviderList = async () => {
  try {
    const res = await getProviderList({});
    if (res.code === 200 && res.data) {
      providerList.value = res.data;
    }
  } catch (error) {
    console.error("获取厂商列表失败:", error);
  }
};

const resetForm = () => {
  formData.id = null;
  formData.cityId = null;
  formData.providerCode = "";
  formData.name = "";
  formData.providerStopId = "";
  formData.address = "";
  formData.coordinates = "";
  formData.gcj02Coordinates = "";
};

const fillForm = (record) => {
  if (!record) {
    resetForm();
    return;
  }
  formData.id = record.id;
  formData.cityId = record.cityId || null;
  formData.providerCode = record.providerCode || "";
  formData.name = record.name || "";
  formData.providerStopId = record.providerStopId || "";
  formData.address = record.address || "";
  formData.coordinates = record.coordinates || "";
  formData.gcj02Coordinates = record.gcj02Coordinates || "";
};

watch(
  () => props.open,
  (val) => {
    if (val) {
      fillForm(props.initialData);
      if (!cityList.value.length) loadCityList();
      if (!providerList.value.length) loadProviderList();
    }
  }
);

const handleCancel = () => {
  emit("update:open", false);
};

const handleSave = () => {
  if (!formData.cityId) {
    message.error("请选择城市");
    return;
  }
  if (!formData.providerCode) {
    message.error("请选择厂商");
    return;
  }
  if (!formData.name) {
    message.error("请输入站点名称");
    return;
  }
  if (!formData.providerStopId) {
    message.error("请输入厂商站点ID");
    return;
  }
  if (!formData.address) {
    message.error("请输入位置");
    return;
  }
  if (!formData.coordinates) {
    message.error("请输入经纬度");
    return;
  }
  if (!formData.gcj02Coordinates) {
    message.error("请输入经纬度（gcj02）");
    return;
  }

  const data = {
    id: formData.id,
    cityId: formData.cityId,
    providerCode: formData.providerCode,
    name: formData.name,
    providerStationId: formData.providerStopId,
    address: formData.address,
    coordinates: formData.coordinates,
    gcj02Coordinates: formData.gcj02Coordinates,
  };

  saving.value = true;
  const api = formData.id ? updateStop : addStop;
  api(data)
    .then((res) => {
      if (res.code === 200) {
        message.success(formData.id ? "修改成功" : "新增成功");
        emit("update:open", false);
        emit("success");
      } else {
        message.error(res.message || (formData.id ? "修改失败" : "新增失败"));
      }
    })
    .catch(() => {
      message.error(formData.id ? "修改失败" : "新增失败");
    })
    .finally(() => {
      saving.value = false;
    });
};
</script>
